import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { Ban, OctagonAlert, Brush, ArrowRight, RefreshCw, Clock, Maximize2, Minimize2, X, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import "leaflet/dist/leaflet.css";
import "./HomeTransitability.css";

const COLORS = {
  4: "#dc2626", // No transitable / tráfico cerrado
  5: "#f97316", // Conflictos sociales
  9: "#eab308", // Obstrucción por limpieza
};

const KIND_META = {
  4: { label: "No transitable", short: "Tráfico cerrado", Icon: Ban, color: "#dc2626" },
  5: { label: "Conflicto social", short: "Conflictos sociales", Icon: OctagonAlert, color: "#f97316" },
  9: { label: "Obstrucción / Limpieza", short: "Obstrucción", Icon: Brush, color: "#eab308" },
};

// Custom SVG-based divIcon for each road state
function buildIcon(idEstado) {
  const color = COLORS[idEstado] || "#888";
  const pulse = idEstado === 5; // conflicts get a subtle in-place highlight
  const svgInner =
    idEstado === 4 ? (
      `<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round">
        <line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>
      </svg>`
    ) : idEstado === 5 ? (
      `<svg viewBox="0 0 24 24" width="11" height="11" fill="#fff" stroke="#fff" stroke-width="0.5">
        <path d="M12 2L1 22h22L12 2zm0 6l7.5 13H4.5L12 8zm-1 4v4h2v-4h-2zm0 5v2h2v-2h-2z"/>
      </svg>`
    ) : (
      `<svg viewBox="0 0 24 24" width="11" height="11" fill="#1f2937">
        <circle cx="6" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="18" cy="12" r="2"/>
      </svg>`
    );
  return L.divIcon({
    className: `trans-marker trans-marker-${idEstado} ${pulse ? "trans-marker-pulse" : ""}`,
    html: `<span class="trans-marker-dot" style="background:${color}">${svgInner}</span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -11],
  });
}

const ICONS = { 4: buildIcon(4), 5: buildIcon(5), 9: buildIcon(9) };

// Bolivia bounding box (fallback when no events)
const BOLIVIA_BOUNDS = [[-22.9, -69.7], [-9.7, -57.5]];
// Hard limits — tight around Bolivia to prevent panning to Argentina/Chile
const BOLIVIA_MAX_BOUNDS = [[-23.3, -70.5], [-9.3, -56.8]];

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) {
      map.fitBounds(BOLIVIA_BOUNDS, { padding: [20, 20] });
      return;
    }
    const latlngs = points.map((p) => [p.lat, p.lng]);
    try {
      map.fitBounds(latlngs, { padding: [40, 40], maxZoom: 8 });
    } catch {
      map.fitBounds(BOLIVIA_BOUNDS, { padding: [20, 20] });
    }
  }, [points, map]);
  return null;
}

// Resize handler: re-invalidates map size and refits bounds when the wrapping container changes
// (e.g. fullscreen toggle). Triggered via a `nonce` prop bumped from parent.
function MapResizer({ nonce, points }) {
  const map = useMap();
  useEffect(() => {
    if (nonce === 0) return;
    setTimeout(() => {
      map.invalidateSize();
      if (points && points.length) {
        try {
          map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 8 });
        } catch {
          map.fitBounds(BOLIVIA_BOUNDS, { padding: [20, 20] });
        }
      }
    }, 120);
  }, [nonce, map, points]);
  return null;
}

function formatRelative(iso) {
  if (!iso) return "";
  // ABC format: "2026-06-19 05:59:19" (assumed local BOL UTC-4)
  let d;
  try {
    d = new Date(iso.replace(" ", "T") + "-04:00");
  } catch { return ""; }
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function stripHtml(s) {
  return (s || "").replace(/<[^>]+>/g, "").trim();
}

export default function HomeTransitability({ variant = "compact" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const mapWrapRef = useRef(null);

  const openDetail = (e) => setSelectedEvent(e);
  const closeDetail = () => setSelectedEvent(null);

  const toggleFullscreen = async () => {
    const el = mapWrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      try { await el.requestFullscreen(); } catch { setFullscreen((f) => !f); return; }
    } else {
      try { await document.exitFullscreen(); } catch { setFullscreen((f) => !f); return; }
    }
  };

  const [resizeNonce, setResizeNonce] = useState(0);

  useEffect(() => {
    const onFs = () => {
      setFullscreen(!!document.fullscreenElement);
      setResizeNonce((n) => n + 1);
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const load = () => {
    setLoading(true);
    api.get("/transitability/public")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(iv);
  }, []);

  const filtered = useMemo(() => {
    if (!data?.events) return [];
    return data.events.filter((e) => e.lat && e.lng);
  }, [data]);

  if (!data || (!loading && !data.enabled)) return null;

  const counts = data.counts || {};

  return (
    <section className="trans-mod" data-testid="home-transitability">
      <div className="trans-head">
        <div className="trans-head-left">
          <h2 className="trans-title">Transitabilidad en Bolivia</h2>
          <p className="trans-sub">Rutas cerradas, conflictos y tramos no transitables</p>
        </div>
        <span className="trans-badge">SERVICIO PÚBLICO</span>
      </div>

      {/* Counter strip */}
      <div className="trans-counts">
        <div className="trans-count trans-count-red" data-testid="trans-count-no-transitable">
          <span className="trans-count-icon"><Ban size={16} /></span>
          <span className="trans-count-num">{counts.no_transitable ?? 0}</span>
          <span className="trans-count-lbl">No transitable</span>
        </div>
        <div className="trans-count trans-count-red" data-testid="trans-count-trafico">
          <span className="trans-count-icon"><Ban size={16} /></span>
          <span className="trans-count-num">{counts.no_transitable ?? 0}</span>
          <span className="trans-count-lbl">Tráfico cerrado</span>
        </div>
        <div className="trans-count trans-count-orange" data-testid="trans-count-conflictos">
          <span className="trans-count-icon"><OctagonAlert size={16} /></span>
          <span className="trans-count-num">{counts.conflictos_sociales ?? 0}</span>
          <span className="trans-count-lbl">Conflictos sociales</span>
        </div>
        <div className="trans-count trans-count-yellow" data-testid="trans-count-limpieza">
          <span className="trans-count-icon"><Brush size={16} /></span>
          <span className="trans-count-num">{counts.limpieza ?? 0}</span>
          <span className="trans-count-lbl">Obstrucción / limpieza</span>
        </div>
      </div>

      <div className={`trans-body ${variant === "full" ? "trans-body-full" : ""}`}>
        {/* Map */}
        <div className={`trans-map-wrap ${fullscreen ? "trans-map-wrap-fs" : ""} ${variant === "full" ? "trans-map-wrap-tall" : ""}`} ref={mapWrapRef}>
          <MapContainer
            center={[-16.5, -65.0]}
            zoom={6}
            minZoom={6}
            maxZoom={12}
            maxBounds={BOLIVIA_MAX_BOUNDS}
            maxBoundsViscosity={1.0}
            scrollWheelZoom={false}
            className="trans-map"
            data-testid="trans-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={filtered} />
            <MapResizer nonce={resizeNonce} points={filtered} />
            {filtered.map((e) => (
              <Marker
                key={e.id}
                position={[e.lat, e.lng]}
                icon={ICONS[e.id_estado] || ICONS[4]}
                eventHandlers={{ click: () => openDetail(e) }}
              >
                <Tooltip direction="top" offset={[0, -10]}>
                  <strong>{e.estado_label}</strong>
                  <br />
                  {e.tramo_inicio} → {e.tramo_fin}
                </Tooltip>
              </Marker>
            ))}
          </MapContainer>

          {/* Map controls (fullscreen + refresh) */}
          <div className="trans-map-controls">
            <button
              type="button"
              className="trans-fs-btn"
              onClick={load}
              disabled={loading}
              title="Actualizar datos"
              data-testid="trans-map-refresh"
            >
              <RefreshCw size={16} className={loading ? "trans-spin" : ""} />
            </button>
            <button
              type="button"
              className="trans-fs-btn"
              onClick={toggleFullscreen}
              title={fullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
              data-testid="trans-fullscreen-toggle"
            >
              {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          </div>

          {/* Legend overlay */}
          <div className="trans-legend">
            <p className="trans-legend-title">Leyenda</p>
            <div className="trans-legend-row">
              <span className="trans-legend-marker trans-legend-4" />
              <span>No transitable / Tráfico cerrado</span>
            </div>
            <div className="trans-legend-row">
              <span className="trans-legend-marker trans-legend-5 trans-legend-pulse" />
              <span>Conflicto social</span>
            </div>
            <div className="trans-legend-row">
              <span className="trans-legend-marker trans-legend-9" />
              <span>Obstrucción / limpieza</span>
            </div>
          </div>
        </div>

        {/* Right column: top alerts */}
        <aside className="trans-side">
          <div className="trans-side-head">
            <span className="trans-side-title">{counts.total_alerts ?? 0} TRAMO(S) EN ALERTA</span>
            <button className="trans-refresh" onClick={load} disabled={loading} title="Actualizar" data-testid="trans-refresh">
              <RefreshCw size={14} className={loading ? "trans-spin" : ""} />
            </button>
          </div>
          <ul className="trans-list">
            {filtered.slice(0, variant === "full" ? 60 : 6).map((e) => {
              const m = KIND_META[e.id_estado] || KIND_META[4];
              return (
                <li key={e.id} className="trans-item" data-testid={`trans-item-${e.id}`}>
                  <button
                    type="button"
                    className="trans-item-btn"
                    onClick={() => openDetail(e)}
                    data-testid={`trans-item-open-${e.id}`}
                  >
                    <span className={`trans-pill trans-pill-${e.id_estado}`}>
                      <m.Icon size={11} /> {m.short.toUpperCase()}
                    </span>
                    <p className="trans-item-loc">{e.tramo_inicio} → {e.tramo_fin}</p>
                    <p className="trans-item-time"><Clock size={10} /> {formatRelative(e.fecha)}</p>
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && !loading && (
              <li className="trans-empty">No hay alertas activas en este momento.</li>
            )}
          </ul>
          <Link to="/transitabilidad" className="trans-cta" data-testid="trans-see-all">
            Ver mapa completo <ArrowRight size={14} />
          </Link>
        </aside>
      </div>

      <div className="trans-foot">
        <span>Fuente: Administradora Boliviana de Carreteras — ABC</span>
        {data.updated_at && <span>Última actualización: {formatRelative(data.updated_at.replace("T", " ").slice(0, 19))}</span>}
      </div>

      {/* Detail modal */}
      {selectedEvent && (
        <div
          className="trans-modal-backdrop"
          onClick={closeDetail}
          data-testid="trans-modal-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div className="trans-modal" onClick={(ev) => ev.stopPropagation()}>
            <button
              type="button"
              className="trans-modal-close"
              onClick={closeDetail}
              aria-label="Cerrar"
              data-testid="trans-modal-close"
            >
              <X size={18} />
            </button>
            <div className="trans-modal-head">
              <span className={`trans-modal-dot trans-modal-dot-${selectedEvent.id_estado}`} />
              <h3 className="trans-modal-title">{selectedEvent.estado_label}</h3>
            </div>
            <div className="trans-modal-body">
              <Row label="Sección" value={`${selectedEvent.tramo_inicio || "—"} → ${selectedEvent.tramo_fin || "—"}`} />
              {selectedEvent.sector && <Row label="Sector" value={selectedEvent.sector} />}
              {selectedEvent.evento_label && <Row label="Evento" value={selectedEvent.evento_label} />}
              {selectedEvent.ruta && <Row label="Ruta" value={`${selectedEvent.ruta}${selectedEvent.seccion ? ` · sección ${selectedEvent.seccion}` : ""}`} />}
              {selectedEvent.tipo_carretera && <Row label="Tipo de carretera" value={selectedEvent.tipo_carretera} />}
              {selectedEvent.horario && <Row label="Horario" value={selectedEvent.horario} />}
              {selectedEvent.horario_corte && <Row label="Horario de corte" value={selectedEvent.horario_corte} />}
              {selectedEvent.restriccion && <Row label="Restricción" value={selectedEvent.restriccion} />}
              {selectedEvent.clima && <Row label="Clima" value={selectedEvent.clima} />}
              {selectedEvent.descripcion && (
                <div className="trans-modal-detail">
                  <span className="trans-modal-row-label">Detalles</span>
                  <p>{stripHtml(selectedEvent.descripcion)}</p>
                </div>
              )}
            </div>
            <div className="trans-modal-foot">
              <span className="trans-modal-coord">
                <MapPin size={11} /> {selectedEvent.lat?.toFixed(4)}, {selectedEvent.lng?.toFixed(4)}
              </span>
              <a
                className="trans-modal-google"
                href={`https://www.google.com/maps/search/?api=1&query=${selectedEvent.lat},${selectedEvent.lng}`}
                target="_blank" rel="noopener noreferrer"
              >
                Abrir en Google Maps →
              </a>
              {selectedEvent.fecha && (
                <span className="trans-modal-time"><Clock size={11} /> {formatRelative(selectedEvent.fecha)}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="trans-modal-row">
      <span className="trans-modal-row-label">{label}:</span>
      <span className="trans-modal-row-value">{value}</span>
    </div>
  );
}
